/**
 * Types for the /cases API — the codename family as a place (virtual:
 * keyed by the family root's codename, no table behind it).
 */

import {
  ApiEnquiryChildRequest,
  ApiEnquirySummary,
  EnquiryChildRequest,
  EnquirySummary,
  transformToEnquiryChildRequest,
  transformToEnquirySummary,
} from './enquiry';
import type { ShareAnchor } from './report-request';

export interface CaseReportSummary {
  id: string;
  codename: string | null;
  lifecycleStage: string;
  createdAt: string | null;
}

export interface CaseDetail {
  familyKey: string;
  rootEntityType: 'enquiry' | 'report_request' | 'report' | string;
  rootEntityId: string;
  teamId: string | null;
  /** Case-level owner (family-wide). Null = unclaimed or unclaimable. */
  assignedAnalystUserId: string | null;
  /** False for self-served report roots, which have no ownership column. */
  claimable: boolean;
  enquiry: EnquirySummary | null;
  reportRequests: EnquiryChildRequest[];
  reports: CaseReportSummary[];
}

export interface ApiCaseDetail {
  family_key: string;
  root_entity_type: string;
  root_entity_id: string;
  team_id: string | null;
  assigned_analyst_user_id?: string | null;
  claimable?: boolean;
  enquiry: ApiEnquirySummary | null;
  report_requests?: ApiEnquiryChildRequest[];
  reports?: {
    id: string;
    codename?: string | null;
    lifecycle_stage: string;
    created_at?: string | null;
  }[];
}

export function transformToCaseDetail(api: ApiCaseDetail): CaseDetail {
  return {
    familyKey: api.family_key,
    rootEntityType: api.root_entity_type,
    rootEntityId: api.root_entity_id,
    teamId: api.team_id,
    assignedAnalystUserId: api.assigned_analyst_user_id ?? null,
    claimable: api.claimable ?? true,
    enquiry: api.enquiry ? transformToEnquirySummary(api.enquiry) : null,
    reportRequests: (api.report_requests ?? []).map(transformToEnquiryChildRequest),
    reports: (api.reports ?? []).map((r) => ({
      id: r.id,
      codename: r.codename ?? null,
      lifecycleStage: r.lifecycle_stage,
      createdAt: r.created_at ?? null,
    })),
  };
}

/** Selected chain node, as carried in the case page's `?item=` param. */
export type CaseItemRef = { type: 'enquiry'; id: string } | { type: 'report_request'; id: string };

export function parseCaseItemParam(value: string | null): CaseItemRef | null {
  if (!value) {
    return null;
  }
  const [prefix, id] = value.split(':', 2);
  if (!id) {
    return null;
  }
  if (prefix === 'enq') {
    return { type: 'enquiry', id };
  }
  if (prefix === 'rr') {
    return { type: 'report_request', id };
  }
  return null;
}

export function caseItemParam(ref: CaseItemRef): string {
  return ref.type === 'enquiry' ? `enq:${ref.id}` : `rr:${ref.id}`;
}

// ---------------------------------------------------------------------------
// Activity feed — GET /cases/{family_key}/activity
// ---------------------------------------------------------------------------

/** Who did the thing: a staff analyst, the client, a network partner,
 * or the platform itself. */
export type CaseActivityActorKind = 'analyst' | 'client' | 'partner' | 'system';

export interface CaseActivityEvent {
  /** Stable synthetic id (`{source}:{row id}`), for React keys. */
  id: string;
  occurredAt: string;
  /** Event vocabulary — see `activityIcon` in the Inbox ActivityFeed. */
  kind: string;
  /** One human line: "Moved to Subjects identified". */
  summary: string;
  /** Optional snippet: the message preview, feedback text, notes. */
  detail: string | null;
  actorKind: CaseActivityActorKind;
  /** Display name when known (analyst resolved via gtintel, client /
   * partner from the message); null for system events. */
  actorName: string | null;
  actorUserId: string | null;
  entityType: string | null;
  entityId: string | null;
  /** The request / report codename the event is about — shown when a
   * case has several. */
  entityCodename: string | null;
}

export interface CaseActivity {
  familyKey: string;
  events: CaseActivityEvent[];
  /** True when the server capped the feed (oldest events dropped). */
  truncated: boolean;
}

export interface ApiCaseActivityEvent {
  id: string;
  occurred_at: string;
  kind: string;
  summary: string;
  detail?: string | null;
  actor_kind: CaseActivityActorKind;
  actor_name?: string | null;
  actor_user_id?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  entity_codename?: string | null;
}

export interface ApiCaseActivity {
  family_key: string;
  events: ApiCaseActivityEvent[];
  truncated?: boolean;
}

export function transformToCaseActivity(api: ApiCaseActivity): CaseActivity {
  return {
    familyKey: api.family_key,
    truncated: api.truncated ?? false,
    events: api.events.map((e) => ({
      id: e.id,
      occurredAt: e.occurred_at,
      kind: e.kind,
      summary: e.summary,
      detail: e.detail ?? null,
      actorKind: e.actor_kind,
      actorName: e.actor_name ?? null,
      actorUserId: e.actor_user_id ?? null,
      entityType: e.entity_type ?? null,
      entityId: e.entity_id ?? null,
      entityCodename: e.entity_codename ?? null,
    })),
  };
}

/** Where the case's sharing control anchors: the family root, since a
 * share reaches everything below it. Null while the case has no team
 * (an enquiry whose sender was never matched) — there is nobody to share
 * it with yet. */
export function caseShareAnchor(caseDetail: CaseDetail): ShareAnchor | null {
  if (caseDetail.teamId === null) {
    return null;
  }
  const id = caseDetail.rootEntityId;
  switch (caseDetail.rootEntityType) {
    case 'enquiry':
      return { kind: 'enquiry', id };
    case 'report_request':
      return { kind: 'request', id };
    case 'report':
      return { kind: 'report', id };
    default:
      return null;
  }
}
